import {
  Component,
  EventEmitter, inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import {
  EmployeeProcedureService,
  EmployeeService,
  InsuranceService,
  ServiceService,
  timeStrToSeconds,
} from '@beautyapp/core';
import {
  filter,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { NZ_MODAL_DATA, NzModalRef } from "ng-zorro-antd/modal";
import { Store } from '@ngxs/store';
import {
  AddAppointmentService,
  EditAppointmentService,
} from '../../../appointment.actions';
import { AppointmentFacade } from '../../appointment-facade.service';
import { NgxPermissionsModule, NgxPermissionsService } from "ngx-permissions";
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzFormModule } from 'ng-zorro-antd/form';
import { InputComponent, SelectComponent, TextareaComponent } from '@beautyapp/form';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CommonModule, AsyncPipe } from '@angular/common';
import { AppointmentProductsComponent } from '../appointment-products/appointment-products.component';
import { AppointmentSpecServicesComponent } from '../appointment-spec-services/appointment-spec-services.component';
import { provideNgxMask } from 'ngx-mask';
import {
  productLinePrice,
  seedAutoFixExpense,
  shouldTrackProductExpense,
} from '../../../visit-modal/shared/seed-auto-fix-expense';

@Component({
    selector: 'beautyapp-appointment-service-add-edit',
    templateUrl: './appointment-service-add-edit.component.html',
    styleUrls: ['./appointment-service-add-edit.component.scss'],
    standalone: true,
    providers: [provideNgxMask()],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        NzGridModule,
        NzFormModule,
        InputComponent,
        SelectComponent,
        TextareaComponent,
        TranslateModule,
        TranslatePipe,
        NzButtonModule,
        NzModalModule,
        NzInputModule,
        NzCheckboxModule,
        NzIconModule,
        AsyncPipe,
        AppointmentProductsComponent,
        AppointmentSpecServicesComponent,
        NgxPermissionsModule
    ]
})
export class AppointmentServiceAddEditComponent implements OnInit, OnDestroy {
  readonly data = inject(NZ_MODAL_DATA);
  public modal: NzModalRef = inject(NzModalRef);
  private service: ServiceService = inject(ServiceService);
  public apartmentFacade: AppointmentFacade = inject(AppointmentFacade);
  private permissionsService: NgxPermissionsService = inject(NgxPermissionsService);
  private fb: UntypedFormBuilder = inject(UntypedFormBuilder);

  get serviceIndex() {
    return this.data.serviceIndex;
  }

  get bookingId() {
    return this.data.bookingId;
  }

  get bookingStatus() {
    return this.data.bookingStatus;
  }

  get appointmentService() {
    return this.data.appointmentService;
  }

  get employee() {
    return this.data.employee;
  }

  get serviceStartTime() {
    return this.data.serviceStartTime;
  }

  get isSpecial() {
    return this.data.isSpecial;
  }

  get isAdditionalService() {
    return this.data.isAdditionalService;
  }

  @Output() addedNewService: EventEmitter<any> = new EventEmitter<any>();
  insurance: any;
  form: UntypedFormGroup;
  services$: Observable<any> = this.service.dropdown();
  timeMask = '00:00';
  sub$: Subject<any> = new Subject<any>();
  serviceId?: string;

  serviceProducts: any[] = [];
  serviceSpecs: any[] = [];
  serviceSelected: any;
  employee$!: Observable<any>;
  outputPercent = 0;
  disabled = false;

  get isEmployee() {
    return !this.permissionsService.getPermission('calendar.view_all');
  }

  constructor(

  ) {
    this.form = this.fb.group({
      serviceId: [null, Validators.required],
      service: [null, Validators.required],
      price: [null, Validators.required],
      quantity: [1, [Validators.required, Validators.min(1)]],
      salePrice: null,
      salePercent: null,
      employeeId: null,
      employeeName: null,
      salaryFixed: null,
      salaryPercent: null,
      salaryPercentFromPrice: false,
      expence: null,
      description: null,
      isAdditionalService: false,
      time: [null, [Validators.required, ValidateTime]],
    });
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }

  ngOnInit(): void {
    console.log(this.apartmentFacade.getCurrentEmployee);
    if (this.appointmentService) {
      this.serviceSelected = this.appointmentService.service;
      this.form.patchValue(this.appointmentService);
      if (
        this.appointmentService.products &&
        this.appointmentService.products.length
      ) {
        this.serviceProducts = this.appointmentService.products;
        this.serviceSpecs = this.appointmentService.specServices;
      }
      if (
        this.appointmentService.specServices &&
        this.appointmentService.specServices.length
      ) {
        this.serviceSpecs = this.appointmentService.specServices;
      }
    }
    if (this.apartmentFacade.employeeId && !this.appointmentService) {
      this.form.patchValue({
        salaryFixed: null,
        employeeId: this.apartmentFacade.getCurrentEmployee.id,
        employeeName: this.apartmentFacade.getCurrentEmployee.user?.fullName,
        salaryPercent: this.apartmentFacade.getCurrentEmployee.outputPercent,
      });
    }

    if (this.isAdditionalService) {
      this.form.patchValue({
        isAdditionalService: this.isAdditionalService,
      });
    }

    this.form
      .get('serviceId')
      ?.valueChanges.pipe(takeUntil(this.sub$))
      .subscribe((input) => {
        if (input) this.getServiceById(input);
      });
  }

  private getEmployeeServices() {
    this.disabled = true;
    this.apartmentFacade.employeeServices$
      .pipe(
        takeUntil(this.sub$),
        filter(
          (res: any) => res.serviceId === this.form.get('serviceId')?.value
        )
      )
      .subscribe({
        next: (res: any) => {
          console.log(res);
          if (res) {
            this.form.patchValue({
              salaryPercent: res.outputPercent,
              salaryFixed: res.fixedSalary,
              employeeName: this.employee.user?.fullName,
              isAdditionalService: this.isAdditionalService,
            });
            this.disabled = false;
          } else {
            this.form.patchValue({
              salaryPercent: this.employee.outputPercent,
              salaryFixed: null,
              employeeName: this.employee.user?.fullName,
              isAdditionalService: this.isAdditionalService,
            });
            this.disabled = false;
          }
        },
        error: (err) => {
          this.disabled = false;
        },
        complete: () => {
          this.disabled = false;
        },
      });
  }

  getServiceById(serviceId: any) {
    console.log(serviceId);
    this.disabled = true;
    this.service
      .getById(serviceId)
      .pipe(
        takeUntil(this.sub$),
        map((res: any) => {
          console.log(res);
          this.serviceSelected = res;
          this.serviceProducts = res.serviceProducts.map((m: any) => ({
            ...m,
            price: productLinePrice(m),
          }));
          const productTotal = this.serviceProducts.reduce(
            (previousValue, currentValue) => {
              return previousValue + +currentValue.price;
            },
            0
          );
          // Was inline here, with `else if (res.autoFix && res.fixSum)` — a
          // negative fixSum is truthy, so it seeded a negative expense. The
          // shared helper gates on `> 0` and falls through to the product sum,
          // and is the same rule the visit form and calendar bulk-add use.
          const expence = seedAutoFixExpense(
            res.autoFix,
            res.fixSum,
            productTotal
          );

          console.log(res.autoPriceView)

          this.form.patchValue({
            service: res,
            time: res.time,
            price: res.autoPriceView ? res.price : null,
            quantity: 1,
            expence: expence,
          });
          console.log(this.form.value);
          return res;
        }),
        switchMap((res: any) => this.apartmentFacade.employeeServices$.pipe(
          tap((res: any) => {
            console.log(res);
            const service = res.find((s: any) => s.serviceId === serviceId);
            if (service) {
              this.form.patchValue({
                salaryPercent: service.outputPercent,
                salaryFixed: service.fixedSalary,
                employeeName: this.employee.user?.fullName,
                isAdditionalService: this.isAdditionalService,
              });
            } else {
              this.form.patchValue({
                salaryPercent: this.employee.outputPercent,
                salaryFixed: null,
                employeeName: this.employee.user?.fullName,
                isAdditionalService: this.isAdditionalService,
              });
            }
          })
        )),
      )
      .subscribe({
        next: (res: any) => {
          this.disabled = false;
        },
        error: (err) => {
          this.disabled = false;
        },
        complete: () => {
          this.disabled = false;
        },
      });
  }

  selected(event: any) {
    if (event) {
      this.getServiceById(event);
    }
  }

  submit(): void {
    for (const key in this.form.controls) {
      this.form.controls[key].markAsDirty();
      this.form.controls[key].updateValueAndValidity();
    }
    if (this.form.invalid) return;
    if (this.appointmentService) {
      const params = {
        ...this.form.value,
        products: this.serviceProducts,
        specServices: this.serviceSpecs,
        serviceIndex: this.serviceIndex,
      };
      this.apartmentFacade.editService(params)
        .pipe(takeUntil(this.sub$))
        .subscribe(() => {
          this.modal.triggerOk().then();
        });
    } else {
      const params = {
        ...this.form.value,
        products: this.serviceProducts,
        specServices: this.serviceSpecs,
      };
      this.apartmentFacade.addNewService(params)
        .pipe(takeUntil(this.sub$))
        .subscribe(() => {
          this.modal.triggerOk().then();
        });
    }
  }

  serviceProductsUpdate($event: any[]) {
    this.serviceProducts = $event;
    // `!fixSum` treated a negative fixed sum as "has a fixed sum" and skipped
    // the refresh; the shared predicate gates on `> 0` instead.
    if (
      shouldTrackProductExpense(
        this.serviceSelected?.autoFix,
        this.serviceSelected?.fixSum
      )
    ) {
      const expence = this.serviceProducts.reduce(
        (previousValue, currentValue) => {
          return previousValue + +currentValue.price;
        },
        0
      );
      this.form.get('expence')?.setValue(expence);
    }
  }

  serviceSpecsUpdate($event: any[]) {
    this.serviceSpecs = $event;
  }

  changePercentage($event: any) {
    if (!$event) {
      this.form.patchValue({
        salePrice: null,
      });
      return;
    }
    const { price } = this.form.value;
    this.form.patchValue({
      salePrice: price && price > 0 ? +(price - (price * $event) / 100).toFixed(2) : null,
    });
  }
}

function ValidateTime(control: AbstractControl): { [key: string]: any } | null {
  if (control.value && timeStrToSeconds(control.value) <= 0) {
    return { minTimeRequired: true };
  }
  return null;
}
